import { Component } from '@theme/component';
import { scrollIntoView } from '@theme/scrolling';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} HeritageRefs
 * @property {HTMLElement[]} [chapters]
 * @property {HTMLAnchorElement[]} [railLinks]
 * @property {HTMLElement} [railFill]
 */

/**
 * Heritage timeline (sections/fishers-heritage-timeline.liquid).
 *
 * Progressive enhancement over statically visible content: tracks the chapter
 * nearest the viewport center to sync the rail's active link and progress
 * fill, reveals chapters as they enter the viewport, and smooth-scrolls when
 * a rail link is clicked. Without JS the rail links work as plain anchors.
 *
 * @extends {Component<HeritageRefs>}
 */
class HeritageTimeline extends Component {
  /** @type {IntersectionObserver | null} */
  #activeObserver = null;

  /** @type {IntersectionObserver | null} */
  #revealObserver = null;

  connectedCallback() {
    super.connectedCallback();

    if (!prefersReducedMotion()) {
      this.setAttribute('data-animate', '');
    }

    this.#observeChapters();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeObserver?.disconnect();
    this.#revealObserver?.disconnect();
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /**
   * Handles clicks on rail links (declared via on:click="/selectChapter").
   * @param {MouseEvent & { target: HTMLAnchorElement }} event
   */
  selectChapter(event) {
    const index = Number(event.target.dataset.index);
    const chapter = this.refs.chapters?.[index];

    if (!chapter) return;

    event.preventDefault();
    scrollIntoView(chapter, { block: 'center' });
    this.#activate(index);
  }

  #observeChapters() {
    const chapters = this.refs.chapters ?? [];

    if (!chapters.length) return;

    // Active chapter = the one crossing a band around the viewport's middle.
    this.#activeObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) continue;
          this.#activate(Number(entry.target.dataset.chapterIndex));
        }
      },
      { rootMargin: '-40% 0px -50% 0px' }
    );

    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          this.#revealObserver?.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    for (const chapter of chapters) {
      this.#activeObserver.observe(chapter);
      this.#revealObserver.observe(chapter);
    }
  }

  /**
   * Marks a rail link as current and updates the progress fill.
   * @param {number} index
   */
  #activate(index) {
    if (Number.isNaN(index)) return;

    this.refs.railLinks?.forEach((link, i) => {
      if (i === index) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    const total = this.refs.chapters?.length ?? 1;
    this.style.setProperty('--heritage-progress', String((index + 1) / total));
  }

  /**
   * Keeps the editor usable: a merchant selecting a chapter block must never
   * stare at an un-revealed (transparent) chapter. The editor scrolls the
   * block into view natively, so no manual scrolling here.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const chapter = target.closest('.heritage__chapter');

    if (!(chapter instanceof HTMLElement) || !this.contains(chapter)) return;

    chapter.classList.add('is-visible');
    this.#activate(Number(chapter.dataset.chapterIndex));
  };
}

if (!customElements.get('heritage-timeline')) {
  customElements.define('heritage-timeline', HeritageTimeline);
}
